import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChildren,
  QueryList,
  ViewChild,
} from '@angular/core';
import { StatusOption } from '../../flag-manager.interfaces';
import { FlagGroup, SentEmail, UnderwritingStatus } from '../../../../shared/interfaces';
import { FlagManagerService } from '../../flag-manager.service';
import { FlagService } from '../../../../flag/flag.service';
import { FlagGroupService } from '../../../../flag/flag.group.service';
import { InspectionService } from '../../../inspection.service';
import { dateTimeAsEasternString } from '../../../../shared/date.helper';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import Sortable from 'sortablejs';

@Component({
  selector: 'app-flag-manager-sidebar',
  templateUrl: './flag-manager-sidebar.component.html',
  styleUrls: ['./flag-manager-sidebar.component.scss'],
})
export class FlagManagerSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() readonly statuses: StatusOption[] = [];
  @Input() groups: any[];
  @Input() countUncategorized: number;
  @Input() comments: number;
  @Input() selectedGroup: any;
  @Input() underwritingStatus: UnderwritingStatus;
  @Input() inspectionId: number;
  @Output() showComments = new EventEmitter<any>();
  @Output() openSelectedGroup = new EventEmitter<FlagGroup>();
  @Output() setIssueNo = new EventEmitter<number>();
  @Output() removeGroup = new EventEmitter<number>();
  @Output() createGroup = new EventEmitter<string>();
  @Output() openGroupModal = new EventEmitter<void>();
  @Output() flagDropped = new EventEmitter<{
    flagId: number;
    targetGroupId: number | null;
    revert: () => void;
  }>();
  @Output() dropReverted = new EventEmitter<void>();
  @Output() groupsReordered = new EventEmitter<any[]>();
  @ViewChildren('dropTarget') dropTargets!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild('includedList', { static: false }) includedListRef!: ElementRef<HTMLDivElement>;
  @ViewChild('excludedList', { static: false }) excludedListRef!: ElementRef<HTMLDivElement>;
  excludedGroups: any[] = [];
  includedGroups: any[] = [];
  dragging = false;
  latestReminderEmail: SentEmail | null = null;
  private recEmailTooltipEl: HTMLElement | null = null;
  private sortables: Sortable[] = [];
  private groupSortables: Sortable[] = [];
  private dropTargetsSub?: Subscription;

  typeMap = {
    2: 'grey-flag.svg',
    3: 'red-flag.svg',
  };

  constructor(
    private flagManagerService: FlagManagerService,
    private flagService: FlagService,
    private flagGroupService: FlagGroupService,
    private inspectionService: InspectionService,
    private toastr: ToastrService,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.flagManagerService.getExcludedGroups().subscribe(groups => (this.excludedGroups = groups));
    this.flagManagerService.getIncludedGroups().subscribe(groups => (this.includedGroups = groups));
    this.loadLatestReminderEmail();
  }

  ngAfterViewInit(): void {
    this.initDropTargets();
    this.initGroupSortables();
    // When groups load async, *ngFor renders drop targets after our first init; re-init when they appear
    this.dropTargetsSub = this.dropTargets?.changes?.subscribe(() => {
      this.initDropTargets();
      this.initGroupSortables();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['groups'] || changes['underwritingStatus']) {
      if (changes['groups']) {
        this.flagManagerService.separateGroups(this.groups);
      }
      setTimeout(() => {
        this.initDropTargets();
        this.initGroupSortables();
      });
    }

    if (changes['selectedGroup']) {
      this.selectedGroupChanges();
    }

    if (changes['inspectionId'] && !changes['inspectionId'].firstChange) {
      this.loadLatestReminderEmail();
    }
  }

  ngOnDestroy(): void {
    this.hideRecEmailTooltip();
    this.dropTargetsSub?.unsubscribe();
    this.destroyDropTargets();
    this.destroyGroupSortables();
  }

  get recEmailTooltipText(): string {
    if (!this.latestReminderEmail?.sentDate) return '';
    return `Rec Due Date Reminder email sent ${dateTimeAsEasternString(this.latestReminderEmail.sentDate)}`;
  }

  openGroup(group: any, issueNo?: number) {
    this.selectedGroup = group;
    this.openSelectedGroup.emit(group);

    if (issueNo != null) {
      this.setIssueNo.emit(issueNo);
    }
  }

  showRecEmailTooltip(event: Event): void {
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;

    this.hideRecEmailTooltip();

    const rect = target.getBoundingClientRect();
    const tooltip = this.renderer.createElement('div') as HTMLElement;
    this.renderer.setAttribute(tooltip, 'role', 'tooltip');
    this.renderer.appendChild(tooltip, this.renderer.createText(this.recEmailTooltipText));

    const arrow = this.renderer.createElement('span') as HTMLElement;
    this.renderer.appendChild(tooltip, arrow);

    const tooltipStyles: Record<string, string> = {
      position: 'fixed',
      top: `${rect.top + rect.height / 2}px`,
      left: `${rect.right + 4}px`,
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#2f353a',
      color: '#ffffff',
      padding: '6px 9px',
      fontSize: '14px',
      borderRadius: '4px',
      whiteSpace: 'nowrap',
      fontWeight: '400',
      fontFamily: 'Lato',
      opacity: '1',
      zIndex: '10000',
      pointerEvents: 'none',
      margin: '0',
    };
    Object.entries(tooltipStyles).forEach(([key, value]) => this.renderer.setStyle(tooltip, key, value));

    const arrowStyles: Record<string, string> = {
      position: 'absolute',
      right: '100%',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '0',
      height: '0',
      borderStyle: 'solid',
      borderWidth: '6px',
      borderColor: 'transparent #2f353a transparent transparent',
    };
    Object.entries(arrowStyles).forEach(([key, value]) => this.renderer.setStyle(arrow, key, value));

    this.renderer.appendChild(document.body, tooltip);
    this.recEmailTooltipEl = tooltip;
  }

  hideRecEmailTooltip(): void {
    if (this.recEmailTooltipEl) {
      this.renderer.removeChild(document.body, this.recEmailTooltipEl);
      this.recEmailTooltipEl = null;
    }
  }

  private async loadLatestReminderEmail(): Promise<void> {
    const inspectionId = this.inspectionId;
    if (!inspectionId) {
      this.latestReminderEmail = null;
      return;
    }

    try {
      const result = (await this.inspectionService.getLatestReminderEmail(inspectionId)) ?? null;
      if (this.inspectionId !== inspectionId) {
        return;
      }
      this.latestReminderEmail = result?.sentDate ? result : null;
    } catch {
      if (this.inspectionId !== inspectionId) {
        return;
      }
      this.latestReminderEmail = null;
    }
  }

  private initDropTargets(): void {
    this.destroyDropTargets();
    if (!this.dropTargets) return;

    this.dropTargets.forEach(ref => {
      const el = ref.nativeElement;

      // Ensure element is in the DOM
      if (!el.isConnected) {
        return;
      }

      const sortable = Sortable.create(el, {
        group: { name: 'flags-xfer', pull: false, put: true },
        sort: false,
        animation: 0, // Disable animation for better performance on Mac
        forceFallback: true, // Use mouse events instead of native drag for better Mac performance
        fallbackTolerance: 0, // Immediate fallback
        swapThreshold: 0.65, // Better swap detection
        ghostClass: 'hidden-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        // Performance optimizations
        delay: 0,
        scroll: false,
        scrollSensitivity: 0,
        scrollSpeed: 0,
        bubbleScroll: false,
        preventOnFilter: false,
        dragoverBubble: false,
        removeCloneOnHide: true,
        emptyInsertThreshold: 5,
        onAdd: evt => {
          const itemEl = evt.item as HTMLElement;
          const flagIdStr = itemEl.dataset.flagId;
          const toEl = evt.to as HTMLElement;
          const groupIdAttr = toEl.dataset.groupId;
          const targetGroupId =
            groupIdAttr && groupIdAttr !== 'null' && groupIdAttr !== '' ? Number(groupIdAttr) : null;
          const flagId = flagIdStr ? Number(flagIdStr) : NaN;

          if (isNaN(flagId)) {
            // Invalid flag, remove from DOM
            itemEl.parentNode && itemEl.parentNode.removeChild(itemEl);
            return;
          }

          const fromEl = evt.from as HTMLElement;
          const oldIndex = evt.oldIndex ?? -1;
          const originalContainer = fromEl;

          const revert = () => {
            if (itemEl.parentNode) {
              itemEl.parentNode.removeChild(itemEl);
            }

            if (originalContainer) {
              const children = Array.from(originalContainer.children);
              const actualChildren = children.filter(
                child =>
                  child.classList.contains('image-card') ||
                  child.hasAttribute('data-flag-id') ||
                  (child as HTMLElement).dataset.flagId,
              );

              if (oldIndex >= 0 && oldIndex < actualChildren.length) {
                const targetChild = actualChildren[oldIndex];
                originalContainer.insertBefore(itemEl, targetChild);
              } else if (actualChildren.length > 0) {
                originalContainer.insertBefore(itemEl, actualChildren[0]);
              } else {
                originalContainer.appendChild(itemEl);
              }
            }
          };

          // Check if status is "Rec Letter Sent" or beyond - show warning and revert
          if (this.underwritingStatus >= UnderwritingStatus.LETTER_SENT) {
            this.toastr.warning('Moving flags is disabled for this status');
            itemEl.parentNode && itemEl.parentNode.removeChild(itemEl);
            revert();
            // Emit event so parent can reload to resync Sortable.js state
            this.dropReverted.emit();
            return;
          }

          itemEl.parentNode && itemEl.parentNode.removeChild(itemEl);

          this.flagDropped.emit({ flagId, targetGroupId, revert });
        },
      });
      this.sortables.push(sortable);
    });
  }

  private destroyDropTargets(): void {
    this.sortables.forEach(s => s.destroy());
    this.sortables = [];
  }

  private initGroupSortables(): void {
    this.destroyGroupSortables();

    // Group reordering is always allowed (regardless of status)
    // Included list: reorder only, no cross-list
    if (this.includedListRef?.nativeElement) {
      const s = Sortable.create(this.includedListRef.nativeElement, {
        group: { name: 'included-groups', pull: false, put: false },
        sort: true,
        animation: 0, // Disable animation for better performance on Mac
        forceFallback: true, // Use mouse events instead of native drag for better Mac performance
        fallbackTolerance: 0, // Immediate fallback
        swapThreshold: 0.65, // Better swap detection
        draggable: '.flag',
        handle: '.cta .icon',
        ghostClass: 'group-drag-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        onEnd: () => {
          // persistGroupOrder will check internally if order actually changed
          this.persistGroupOrder();
        },
      });
      this.groupSortables.push(s);
    }
  }

  private destroyGroupSortables(): void {
    this.groupSortables.forEach(s => s.destroy());
    this.groupSortables = [];
  }

  private persistGroupOrder(): void {
    const includedIds = this.readIdsFromList(this.includedListRef);

    const newOrder = [...includedIds];
    if (!newOrder.length) return;

    // Get current order from the groups arrays
    const currentIncludedIds = this.includedGroups.map(g => g.id);
    const currentOrder = [...currentIncludedIds];

    // Check if order has actually changed
    const hasOrderChanged =
      newOrder.length !== currentOrder.length || newOrder.some((id, index) => id !== currentOrder[index]);

    if (!hasOrderChanged) {
      // Order hasn't changed, no need to update
      return;
    }

    setTimeout(() => {
      this.selectedGroupChanges();
    }, 0);

    const groupsMap = new Map(this.groups.map(g => [g.id, g]));
    const reorderedGroups = newOrder.map(id => groupsMap.get(id)).filter(Boolean);
    const remainingGroups = this.groups.filter(g => !newOrder.includes(g.id));
    const finalGroups = [...reorderedGroups, ...remainingGroups];

    // Update local groups reference to prevent ngOnChanges from reverting
    this.groups = finalGroups;
    this.flagManagerService.separateGroups(finalGroups);

    // Emit new order to parent so it stays in sync
    this.groupsReordered.emit(finalGroups);

    this.flagGroupService
      .updateOrder(this.inspectionId, newOrder)
      .then(() => {
        this.toastr.success('You have successfully reordered the groups');
      })
      .catch(() => {
        this.toastr.error('Failed to update group order');
      });
  }

  private readIdsFromList(ref?: ElementRef<HTMLDivElement>): number[] {
    if (!ref?.nativeElement) return [];
    const items = Array.from(ref.nativeElement.querySelectorAll('.flag[data-group-id]')) as HTMLElement[];
    return items.map(el => Number(el.dataset.groupId)).filter(id => Number.isFinite(id));
  }

  selectedGroupChanges(): void {
    if (!this.selectedGroup || !Array.isArray(this.includedGroups)) return;
    const index = this.includedGroups.findIndex(group => group.id === this.selectedGroup.id);
    if (index !== -1) {
      this.setIssueNo.emit(index + 1);
    }
  }

  openConfirmationModal(group, index) {
    this.openGroup(group, index);
    this.removeGroup.emit(this.selectedGroup);
  }

  isChecked(group: any) {
    const found = this.groups?.find(g => g.id === group.id);
    return found?.checkbox?.value === true;
  }

  anyCheckboxChecked() {
    return this.groups?.some(flag => flag.checkbox?.value);
  }

  isGroupComplete(group: any): boolean {
    if (!group) return false;
    return this.flagService.isCompleted(group);
  }

  getGroupIcon(group: any): string {
    if (this.isGroupComplete(group)) {
      return 'assets/img/check.svg';
    }
    return 'assets/img/svg/group-detail.svg';
  }

  onAddGroup() {
    this.openGroupModal.emit();
  }

  onCreateGroup(groupName: string) {
    this.createGroup.emit(groupName);
  }

  private previousScrollTop: number | null = null;

  onDeleteButtonHover(index: number): void {
    if (index === this.excludedGroups.length - 1) {
      const container = document.querySelector('.rec-groups') as HTMLElement;
      if (container) {
        if (this.previousScrollTop === null) this.previousScrollTop = container.scrollTop;
        container.scrollTop = container.scrollHeight - container.clientHeight;
      }
    }
  }

  onDeleteButtonLeave(): void {
    const container = document.querySelector('.rec-groups') as HTMLElement;
    if (container && this.previousScrollTop !== null) {
      container.scrollTop = this.previousScrollTop;
      this.previousScrollTop = null;
    }
  }
}
